import { DOCUMENT } from '@angular/common';
import { inject, InjectionToken } from '@angular/core';
import { StorageFactory } from './storage.factory';

/**
 * Safe localStorage access with automatic in-memory fallback.
 *
 * Uses Angular's DOCUMENT injection token to access localStorage through the
 * DI system (SSR-safe). StorageFactory handles try-catch and falls back to
 * in-memory storage when browser storage is unavailable (Safari privacy mode,
 * quota exceeded, disabled by user).
 *
 * @example
 * ```typescript
 * readonly #storage = inject(LOCAL_STORAGE);
 * this.#storage.getItem('key');
 * this.#storage.setItem('key', 'value');
 * ```
 */
export const LOCAL_STORAGE = new InjectionToken<StorageFactory>('SafeLocalStorage', {
  providedIn: 'root',
  factory: () => {
    const win = inject(DOCUMENT).defaultView;
    return new StorageFactory(() => win?.localStorage as Storage);
  },
});

/**
 * Safe sessionStorage access with automatic in-memory fallback.
 *
 * Uses Angular's DOCUMENT injection token to access sessionStorage through the
 * DI system (SSR-safe). StorageFactory handles try-catch and falls back to
 * in-memory storage when browser storage is unavailable (Safari privacy mode,
 * quota exceeded, disabled by user).
 *
 * @example
 * ```typescript
 * readonly #storage = inject(SESSION_STORAGE);
 * this.#storage.getItem('key');
 * this.#storage.setItem('key', 'value');
 * ```
 */
export const SESSION_STORAGE = new InjectionToken<StorageFactory>('SafeSessionStorage', {
  providedIn: 'root',
  factory: () => {
    const win = inject(DOCUMENT).defaultView;
    return new StorageFactory(() => win?.sessionStorage as Storage);
  },
});
